/** @jsxImportSource @emotion/react */
import React, { useContext, useState } from 'react';
import { StylesClassName, UseStyles } from 'types/styles';
import { Button, Dialog, DialogContent, DialogProps, IconButton, Theme, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import setSpacing from 'utils/spacing';
import { asset } from 'utils/asset';
import { useLang } from 'contexts/intl';
import { FormattedMessage } from 'react-intl';
import { config } from 'application';
import { AppContext } from 'App';

interface AccessDialogProps extends Omit<DialogProps, 'open' | 'className'> {
  className?: StylesClassName;
  onClose?: () => void;
  open?: boolean;
}

const AccessDialog: React.FC<AccessDialogProps> = ({ className, open = false, onClose, ...props }) => {
  const theme = useTheme() as Theme;
  const classes = useStyles(theme);
  const { lang } = useLang();
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const { isAccessRequestOpen, setIsAccessRequestOpen } = useContext(AppContext);

  const handleCloseBtnClick = () => {
    setIsAccessRequestOpen?.(false);
    onClose?.();
  };
  const handleHelpBtnClick = () => {
    setIsHelpOpen(true);
  };
  const handleHelpCloseBtnClick = () => {
    setIsHelpOpen(false);
  };

  return (
    <>
      <Dialog open={isAccessRequestOpen} css={[classes.AccessDialog, className]} {...props}>
        <DialogContent css={classes.content}>
          <img src={asset('/images/logo.svg')} alt="" width={445} css={classes.logo} />
          <div css={classes.title}>
            <FormattedMessage id={'AccessDialog.requestAccess'} defaultMessage={'Request Access'} />
          </div>
          <div css={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              href={`${config.partnersLink}/${lang}/sign-in-azure`}
              target="_blank"
              rel="noreferrer"
            >
              <FormattedMessage id={'AccessDialog.continueWHOAccount'} defaultMessage={'Continue with WHO account'} />
            </Button>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              href={`${config.partnersLink}/${lang}/request-access-email`}
              target="_blank"
              rel="noreferrer"
            >
              <FormattedMessage id={'AccessDialog.continueEmail'} defaultMessage={'Continue with email'} />
            </Button>
          </div>
          <div css={classes.desc}>
            <span>
              <FormattedMessage id={'AccessDialog.alreadyHaveAccount'} defaultMessage={'Already have an account?'} />{' '}
              <a href={`${config.partnersLink}/${lang}/sign-in-azure`} target="_blank" rel="noreferrer">
                <FormattedMessage id={'AccessDialog.signInHere'} defaultMessage={'Sign in here'} />
              </a>
            </span>
            <span>
              <FormattedMessage id={'AccessDialog.click'} defaultMessage={'Click'} />{' '}
              <button css={classes.helpBtn} onClick={handleHelpBtnClick}>
                <FormattedMessage id={'AccessDialog.here'} defaultMessage={'here'} />
              </button>{' '}
              <FormattedMessage
                id={'AccessDialog.learnMoreAbout'}
                defaultMessage={'to learn more about how prospective users request access to the Partners Platform'}
              />
            </span>
          </div>
        </DialogContent>
        <IconButton css={classes.closeBtn} onClick={handleCloseBtnClick}>
          <CloseIcon />
        </IconButton>
      </Dialog>

      <Dialog open={isHelpOpen} css={classes.HelpDialog}>
        <DialogContent css={classes.content}>
          <video width="100%" controls autoPlay>
            <source
              type="video/mp4"
              src="https://partnersporttestcdn.blob.core.windows.net/video/pp_registration_v2.mp4"
            ></source>
            Browser not supported
          </video>
        </DialogContent>
        <IconButton css={classes.closeBtn} onClick={handleHelpCloseBtnClick}>
          <CloseIcon />
        </IconButton>
      </Dialog>
    </>
  );
};

const useStyles = (theme: Theme): UseStyles => ({
  AccessDialog: {
    '& .MuiPaper-root': {
      width: '40vw',
    },
    '@media (max-width: 960px)': {
      '& .MuiPaper-root': {
        width: '100%',
      },
    },
  },
  content: {
    margin: 0,
    padding: setSpacing([10, 8, 7]),
  },
  closeBtn: {
    position: 'absolute',
    right: 8,
    top: 8,
  },
  logo: {
    display: 'block',
    width: '100%',
  },
  title: {
    marginTop: theme.spacing(8),
    color: theme.palette.custom.primary.brand[100],
    fontWeight: 600,
    fontSize: 32,
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    marginTop: theme.spacing(4),
  },
  desc: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontSize: 11,
    lineHeight: '20px',
    marginTop: theme.spacing(4),

    a: {
      color: theme.palette.custom.primary.brand[110],
    },
    button: {
      background: 'none',
      margin: 0,
      padding: 0,
      border: 0,
      cursor: 'pointer',
      fontSize: 11,
      fontWeight: 400,
      fontFamily: theme.typography.fontFamily,
      textDecoration: 'underline',
      color: theme.palette.custom.primary.brand[110],

      '&:hover': {
        textDecoration: 'none',
      },
    },
  },
  HelpDialog: {
    '& .MuiPaper-root': {
      maxWidth: 960,
    },
  },
});

export default AccessDialog;
